import { Link } from "react-router-dom";
import { HandHeart, MessageCircleHeart, Home as HomeIcon, Sprout, Search, Lock, ArrowRight } from "lucide-react";
import PublicLayout, { LOGO, ScamNotice, usePublicSettings } from "@/components/PublicLayout";
import { NewsStrip } from "@/components/NewsStrip";
import { useLang } from "@/lib/i18n";

const TILES = [
  { to: "/request/assistance", k: "t_assist", icon: HandHeart, id: "tile-need-assistance", tone: "bg-[#4A2E1A] text-[#FDFBF7]", sub: "text-[#E9DCCF]", big: true },
  { to: "/request/talk", k: "t_talk", icon: MessageCircleHeart, id: "tile-someone-to-talk", tone: "bg-[#3D6346] text-white", sub: "text-[#D5E5D8]", big: true },
  { to: "/request/home_visit", k: "t_visit", icon: HomeIcon, id: "tile-home-visit", tone: "bg-white text-[#241D17] border border-[#E7DFD5]", sub: "text-[#605349]" },
  { to: "/help", k: "t_help", icon: Sprout, id: "tile-want-to-help", tone: "bg-[#FDF7E7] text-[#241D17] border border-[#E9D49A]", sub: "text-[#605349]" },
  { to: "/check", k: "t_check", icon: Search, id: "tile-check-request", tone: "bg-white text-[#241D17] border border-[#E7DFD5]", sub: "text-[#605349]" },
  { to: "/office/login", k: "t_login", icon: Lock, id: "tile-office-login", tone: "bg-[#F4EBE4] text-[#241D17] border border-[#E2CDBD]", sub: "text-[#605349]" },
];

const Tile = ({ tile, t, i }) => {
  const Icon = tile.icon;
  return (
    <Link to={tile.to} data-testid={tile.id} style={{ animationDelay: `${i * 60}ms` }}
      className={`fade-up group relative rounded-2xl p-6 sm:p-7 min-h-[150px] flex flex-col justify-between shadow-[0_1px_0_rgba(74,46,26,0.06)] hover:shadow-[0_10px_30px_-12px_rgba(74,46,26,0.35)] transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-[#C59B27] focus:ring-offset-2 ${tile.tone} ${tile.big ? "sm:col-span-1 lg:row-span-1" : ""}`}>
      <Icon className="w-8 h-8" strokeWidth={1.6} />
      <div className="mt-6">
        <p className="text-lg sm:text-xl font-extrabold uppercase tracking-wide leading-tight">{t(tile.k)}</p>
        <p className={`mt-1.5 text-[15px] leading-snug ${tile.sub}`}>{t(`${tile.k}_d`)}</p>
      </div>
      <ArrowRight className="absolute top-6 right-6 w-5 h-5 opacity-60 group-hover:translate-x-1 transition-transform duration-200" />
    </Link>
  );
};

export default function Home() {
  const { t } = useLang();
  const settings = usePublicSettings();
  return (
    <PublicLayout>
      <NewsStrip settings={settings} />
      <section className="relative overflow-hidden grain">
        <div className="max-w-6xl mx-auto px-5 pt-10 sm:pt-14 pb-10 relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="order-2 md:order-1">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#3D6346]">Parish Pastoral Care and Assistance Center</p>
          <h1 data-testid="home-title" className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] max-w-3xl">{t("heroTitle")}</h1>
          <p className="mt-5 text-base md:text-lg text-[#241D17] max-w-2xl leading-relaxed">{t("heroBody")}</p>
          <p className="mt-6 inline-block border-l-4 border-[#C59B27] pl-4 font-serif-head italic text-[#4A2E1A] text-base sm:text-lg">{t("tagline")}</p>
          </div>
          <div className="order-1 md:order-2 flex md:block justify-center">
            <img src={LOGO} alt="Saint Francis of Assisi Parish, Mabuhay, General Santos City — Pax et Bonum" data-testid="home-parish-logo" width="480" height="468" className="w-44 h-44 sm:w-56 sm:h-56 lg:w-72 lg:h-72 object-contain drop-shadow-[0_12px_30px_rgba(74,46,26,0.18)] fade-up" />
          </div>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-5 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {TILES.map((tile, i) => <Tile key={tile.id} tile={tile} t={t} i={i} />)}
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6 text-[15px] text-[#605349]">
          <p><span className="block font-bold text-[#4A2E1A] mb-1">Every request is acknowledged.</span>You receive a reference number and a private PIN right away.</p>
          <p><span className="block font-bold text-[#4A2E1A] mb-1">Reviewed by real people.</span>Authorized parish personnel — never an automated system — decide how to respond.</p>
          <p><span className="block font-bold text-[#4A2E1A] mb-1">Kept confidential.</span>Only the people assigned to help you can see your details. <Link to="/privacy" className="underline">Read our privacy notice</Link>.</p>
        </div>
        <div className="mt-10"><ScamNotice /></div>
      </section>
    </PublicLayout>
  );
}
